import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { theme } from '@hhgtech/hhg-components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { Select, Text } from '@hhgtech/hhg-components/mantine'
import { ChevronDown } from '@hhgtech/icons/arrow'
import { Box, rem } from '@mantine/core'
import cls from 'classnames'
import { useIntl } from 'react-intl'

import { useDueDateStore } from 'components/templates/dueDateCalculator/utils'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ArticleApi, TogetherTopic } from 'interfaces/types'
import { DueDateInfoApi } from 'interfaces/types/DueDate'

export enum DueDateNavResultTarget {
  due_date_revealed = 'due_date_revealed',
  guide_and_support = 'guide_and_support',
  helpful_tips_for_mom = 'helpful_tips_for_mom',
  faq = 'faq',
}

type Props = {
  topics: TogetherTopic[]
  suggestedArticles: ArticleApi[]
  isShowFaqs: boolean
  sponsorBanner: DueDateInfoApi['sponsor_banner'] | undefined
  hasHelpfulTips: boolean
}

const mapTrackingEventLabel: Record<string, string> = {
  [DueDateNavResultTarget.due_date_revealed]: 'Due Date Revealed',
  [DueDateNavResultTarget.guide_and_support]: 'Guide and Support',
  [DueDateNavResultTarget.helpful_tips_for_mom]: 'Helpful Tips',
  [DueDateNavResultTarget.faq]: 'FAQ',
}

const useOnClickNav = () => {
  const { setNavResultTarget } = useDueDateStore()

  const handleClick = useCallback((id: DueDateNavResultTarget) => {
    setNavResultTarget(id)
    const el = document.getElementById(id)
    if (el) {
      const offset = 100 // Fixed-Menu's height
      const top = el.getBoundingClientRect().top + window.scrollY - offset

      window.scrollTo({ top, behavior: 'smooth' })
    }
  }, [])

  return { handleClick }
}

const BoxWithAnimation = ({
  className,
  children,
  name,
}: {
  className?: string
  children?: ReactNode
  name: DueDateNavResultTarget
}) => {
  const { handleClick } = useOnClickNav()
  const tracking = useMemo(() => {
    return {
      'data-event-category': 'Health Tool',
      'data-event-action': 'Due Date - Doctor Suggestion',
      'data-event-label': mapTrackingEventLabel[name],
    }
  }, [name])

  return (
    <Box
      py={10}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.3s ease',
        color: theme.colors.gray400,
        ':before': {
          content: '""',
          display: 'block',
          width: 0,
          height: 2,
          backgroundColor: theme.colors.primaryBase,
          position: 'absolute',
          top: 24,
          left: 0,
          opacity: 0,
          transition: 'all 0.3s ease',
        },
        ':hover': {
          color: theme.colors.primary600,
        },
        '&.active': {
          paddingLeft: 36,
          color: theme.colors.primaryBase,
          ':before': {
            opacity: 1,
            width: 24,
          },
        },
      }}
      onClick={() => handleClick(name)}
      className={cls(className, 'pointer-event-child-none')}
      {...tracking}
    >
      {children}
    </Box>
  )
}

const DesktopNavResult = ({
  suggestedArticles,
  topics,
  isShowFaqs,
  sponsorBanner,
  hasHelpfulTips,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const ref = useRef<HTMLDivElement | null>(null)
  const { locale } = useRouterExtend()
  const { navResultTarget } = useDueDateStore()
  const [spacingBottom, setSpacingBottom] = useState(120)
  const { handleClick } = useOnClickNav()
  const hasArticlesSection = !!suggestedArticles.length
  const hasCommunityBanner =
    LOCALE_HAS_COMMUNITY_SITE.includes(locale) && !!topics?.length
  // const hasRelatedTools =

  useEffect(() => {
    const $wrapper = ref.current

    if (!$wrapper || isMobile) return

    const handler = () => {
      const offsetTop = 250
      const heightOfDisclaimer = 110
      const heightOfWindow = window.innerHeight
      const rect = $wrapper.getBoundingClientRect()
      const height = rect.height
      const spacingBottom =
        heightOfWindow - offsetTop - height - heightOfDisclaimer

      setSpacingBottom(Math.max(120, spacingBottom))
    }
    handler()
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [isMobile, suggestedArticles, topics])

  const listNav = useMemo(() => {
    return [
      {
        value: DueDateNavResultTarget.due_date_revealed,
        label: f({
          id: 'dueDatePage.result.due_date_revealed',
          defaultMessage: 'Your Due Date Revealed!',
        }),
      },
      hasArticlesSection || hasCommunityBanner || sponsorBanner?.image
        ? {
            value: DueDateNavResultTarget.guide_and_support,
            label: f({
              id: 'dueDatePage.result.guide_and_support',
              defaultMessage: 'Your Pregnancy Guide and Support',
            }),
          }
        : null,
      // This section will be implemented later
      hasHelpfulTips && {
        value: DueDateNavResultTarget.helpful_tips_for_mom,
        label: f({
          id: 'dueDatePage.result.helpful_tips',
          defaultMessage: 'Helpful Tips for Pregnant Mom',
        }),
      },
      isShowFaqs
        ? {
            value: DueDateNavResultTarget.faq,
            label: f({
              id: 'faq.title',
              defaultMessage: 'Frequently Asked Questions',
            }),
          }
        : null,
    ].filter(Boolean) as Array<{
      value: DueDateNavResultTarget
      label: string
    }>
  }, [hasArticlesSection, hasCommunityBanner, hasHelpfulTips])

  if (isMobile) {
    return (
      <Box mt={0} pos="sticky" top={0} data-ahihi="true" sx={{ zIndex: 3 }}>
        <Select
          data={listNav}
          value={navResultTarget}
          styles={{
            root: {
              '[aria-expanded="true"]': {
                '.right-section-icon': {
                  transform: 'rotate(180deg)',
                },
              },
            },
            input: {
              borderRadius: 0,
              border: '0',
              paddingLeft: rem(16),
              backgroundColor: theme.colors.white,
              ':focus': { boxShadow: 'none' },
              ':not(.mantine-Textarea-input)': {
                height: 44,
                fontSize: 16,
              },
            },
            dropdown: {
              borderRadius: 0,
              borderLeft: 0,
              borderRight: 0,
              transform: 'translateY(-8px)',
            },
            item: {
              fontSize: 16,
              lineHeight: 1.2,
              wordBreak: 'break-word',
              '&[data-selected]': {
                fontWeight: 600,
                color: theme.colors.primaryBase,
                backgroundColor: 'transparent',
              },
            },
          }}
          withinPortal
          onChange={handleClick}
          rightSection={<ChevronDown className="right-section-icon" />}
        />
      </Box>
    )
  }

  return (
    <Box ref={ref} mt={16} mb={spacingBottom}>
      {listNav.map((navItem) => {
        return (
          <BoxWithAnimation
            key={navItem.value}
            className={cls({
              active: navResultTarget === navItem.value,
            })}
            name={navItem.value}
          >
            <Text size="p1" weight="semiBold" color="inherit">
              {navItem.label}
            </Text>
          </BoxWithAnimation>
        )
      })}
    </Box>
  )
}

export default DesktopNavResult
